import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios from 'axios';
import { getAppText } from 'src/i18n';
import { showMessage } from '../../components/components/SnackbarNotification/snackbarNotificationSlice';

export interface OfficeDto {
    [key: string]: any;
    id: number;
    nameFr: string;
    nameEn: string;
    region_Id: number;
}

export interface OfficeState {
    offices: OfficeDto[];
    office: OfficeDto;
    gettingOffices: boolean;
    gettingOffice: boolean;
    archivingOffice: boolean;
    savingOffice: boolean;
    status: 'idle' | 'loading' | 'succeeded' | 'failed';
    error?: string;
}

const blankOfficeDto: OfficeDto = {
    id: 0,
    nameFr: '',
    nameEn: '',
    region_Id: 0,
};

const initialState: OfficeState = {
    offices: [],
    office: blankOfficeDto,
    gettingOffices: false,
    gettingOffice: false,
    archivingOffice: false,
    savingOffice: false,
    status: 'idle',
    error: undefined,
};

export const officeSlice = createSlice({
    name: 'offices',
    initialState,
    reducers: {
        clearOffices: (state) => {
            state.offices = [];
            state.office = blankOfficeDto;
        },
        updateOffice: (state, param) => {
            const { payload } = param;
            state.office = payload;
        },
    },
    extraReducers(builder) {
        builder
            .addCase(getOffices.pending, (state, action) => {
                state.gettingOffices = true;
                state.status = 'loading';
            })
            .addCase(getOffices.fulfilled, (state, action) => {
                state.gettingOffices = false;
                state.offices = action.payload;
                state.status = 'succeeded';
            })
            .addCase(getOffices.rejected, (state, action) => {
                state.gettingOffices = false;
                state.offices = [];
                state.status = 'failed';
                state.error = action.error.message;
            })
            .addCase(getOffice.pending, (state, action) => {
                state.gettingOffice = true;
                state.status = 'loading';
            })
            .addCase(getOffice.fulfilled, (state, action) => {
                state.gettingOffice = false;
                state.office = action.payload;
                state.status = 'succeeded';
            })
            .addCase(getOffice.rejected, (state, action) => {
                state.gettingOffice = false;
                state.office = blankOfficeDto;
                state.status = 'failed';
                state.error = action.error.message;
            })
            .addCase(saveOffice.pending, (state, action) => {
                state.savingOffice = true;
                state.status = 'loading';
            })
            .addCase(saveOffice.fulfilled, (state, action) => {
                state.savingOffice = false;
                state.status = 'succeeded';
            })
            .addCase(saveOffice.rejected, (state, action) => {
                state.savingOffice = false;
                state.status = 'failed';
                state.error = action.error.message;
            })
            .addCase(archiveOffice.pending, (state, action) => {
                state.archivingOffice = true;
                state.status = 'loading';
            })
            .addCase(archiveOffice.fulfilled, (state, action) => {
                state.archivingOffice = false;
                state.offices = state.offices.filter((x) => x.id !== action.payload.id);
                state.status = 'succeeded';
            })
            .addCase(archiveOffice.rejected, (state, action) => {
                state.archivingOffice = false;
                state.status = 'failed';
                state.error = action.error.message;
            });
    },
});

export const { clearOffices } = officeSlice.actions;
export const { updateOffice } = officeSlice.actions;

export const getOffices = createAsyncThunk('offices/getOffices', async (_, thunkAPI) => {
    const response = await axios.get(`/api/synergy/offices`);

    return response.data;
});

export const getOffice = createAsyncThunk('offices/getOffice', async (id: number, thunkAPI) => {
    const response = await axios.get(`/api/synergy/offices/${id}`);

    return response.data;
});

export const archiveOffice = createAsyncThunk('offices/archiveOffice', async (id: number, thunkAPI) => {
    const response = await axios.post(`/api/synergy/offices/archive/${id}`);
    thunkAPI.dispatch(
        showMessage({
            type: 'info',
            message: getAppText('archivedOffice'),
        })
    );
    return response.data;
});

export const saveOffice = createAsyncThunk('offices/saveOffice', async (officeToSave: OfficeDto, thunkAPI) => {
    if (officeToSave.id === 0) {
        const response = await axios.post(`/api/synergy/offices/create`, officeToSave);
        thunkAPI.dispatch(
            showMessage({
                type: 'info',
                message: getAppText('createOffice'),
            })
        );
        return response.data;
    } else {
        const response = await axios.put(`/api/synergy/offices/put`, officeToSave);
        thunkAPI.dispatch(
            showMessage({
                type: 'info',
                message: getAppText('saveOffice'),
            })
        );
        return response.data;
    }
});
